/*
 * Iter XX.40 — Gully Email Health & Reply Queue (founder dashboard).
 *
 * Two sections in one file:
 *   1. EmailHealthSection — provider + domain + 24h counters + last events.
 *   2. EmailReplyQueue — inbound replies with opportunity linkage.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";

export function EmailHealthSection() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const { data } = await api.get("/admin/email/health");
        if (alive) { setData(data); setErr(null); }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Could not load email health");
      }
    }
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const canSend = !!data?.can_send;

  return (
    <section data-testid="section-email-health" className="space-y-4">
      <div className="mb-1">
        <p className="label-eyebrow">email health · gully communication identity</p>
        <p className="text-xs text-neutral-500 mt-0.5">
          provider · domain · deliverability · reply queue · phase 1
        </p>
      </div>

      {err && <div className="surface-1 p-4 text-red-400 text-sm" data-testid="email-health-error">{err}</div>}
      {!data && !err && <div className="surface-1 p-4 text-neutral-500 text-sm">Loading…</div>}

      {data && (
        <>
          <div className="surface-1 p-4 flex flex-wrap gap-4 items-center" data-testid="email-health-status">
            <PillBadge
              label="provider"
              ok={data.provider?.configured}
              value={data.provider?.name || "—"}
              testid="email-provider-badge"
            />
            <PillBadge
              label="api key"
              ok={data.provider?.configured}
              value={data.provider?.configured ? "present" : "missing"}
              testid="email-apikey-badge"
            />
            <PillBadge
              label="webhook"
              ok={data.provider?.webhook_secret_configured}
              value={data.provider?.webhook_secret_configured ? "signed" : "not configured"}
              testid="email-webhook-badge"
            />
            <PillBadge
              label="inbound"
              ok={data.provider?.inbound_enabled}
              value={data.provider?.inbound_enabled ? "enabled" : "disabled"}
              testid="email-inbound-badge"
            />
            <PillBadge
              label="sender"
              ok={data.sender_valid}
              value={data.sender?.from_address}
              testid="email-sender-badge"
            />
            <PillBadge
              label="domain"
              ok={data.domain?.verified}
              value={data.domain?.domain || "—"}
              testid="email-domain-badge"
            />
            <PillBadge label="spf"   ok={statusOk(data.domain?.spf)}   value={data.domain?.spf?.status || "—"}   testid="email-spf-badge" />
            <PillBadge label="dkim"  ok={statusOk(data.domain?.dkim)}  value={data.domain?.dkim?.status || "—"}  testid="email-dkim-badge" />
            <PillBadge label="dmarc" ok={statusOk(data.domain?.dmarc)} value={data.domain?.dmarc?.status || "—"} testid="email-dmarc-badge" />
            <div className={`ml-auto text-xs px-2 py-1 rounded border ${
              canSend
                ? "border-emerald-800 text-emerald-400"
                : "border-amber-800 text-amber-400"
            }`} data-testid="email-cansend-badge">
              {canSend ? "can send" : `blocked · ${data.blocking_reason || "unverified"}`}
            </div>
          </div>

          {/* DNS records still required */}
          {(data.domain?.records_missing || []).length > 0 && (
            <div className="surface-1 p-4" data-testid="email-dns-required">
              <div className="label-eyebrow mb-2">dns records required</div>
              <div className="space-y-2 text-xs font-mono">
                {data.domain.records_missing.map((r, i) => (
                  <div key={i} className="border border-neutral-800 rounded p-2">
                    <div className="text-neutral-500">
                      {typeof r === "string" ? r : `${r.type || "?"} · ${r.name || "?"}`}
                    </div>
                    {typeof r === "object" && r.value && (
                      <div className="text-neutral-300 break-all mt-1">{r.value}</div>
                    )}
                    {typeof r === "object" && r.status && (
                      <div className="text-amber-400 mt-1">status: {r.status}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 24h counters */}
          <div className="surface-1 p-4" data-testid="email-counters">
            <div className="label-eyebrow mb-2">last 24 hours</div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <Counter label="attempted"          v={data.counters_24h?.attempted} testid="ec-attempted" />
              <Counter label="provider accepted"  v={data.counters_24h?.provider_accepted} testid="ec-accepted" />
              <Counter label="delivered"          v={data.counters_24h?.delivered} testid="ec-delivered" />
              <Counter label="opened"             v={data.counters_24h?.opened} testid="ec-opened" />
              <Counter label="clicked"            v={data.counters_24h?.clicked} testid="ec-clicked" />
              <Counter label="bounced"            v={data.counters_24h?.bounced} testid="ec-bounced" tone={data.counters_24h?.bounced ? "warn" : ""} />
              <Counter label="complained"        v={data.counters_24h?.complained} testid="ec-complained" tone={data.counters_24h?.complained ? "warn" : ""} />
              <Counter label="failed"             v={data.counters_24h?.failed} testid="ec-failed" tone={data.counters_24h?.failed ? "warn" : ""} />
              <Counter label="blocked pre-send"   v={data.counters_24h?.blocked_pre_send} testid="ec-blocked" />
              <Counter label="unsubscribed"       v={data.counters_24h?.unsubscribed} testid="ec-unsubscribed" />
              <Counter label="replied"            v={data.counters_24h?.replied} testid="ec-replied" />
              <Counter label="suppression list"   v={data.suppression_total} testid="ec-suppression" />
            </div>
          </div>

          {/* Last successful send + last error */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="surface-1 p-4" data-testid="email-last-ok">              <div className="label-eyebrow mb-1">last successful send</div>
              {data.last_successful_send ? (
                <div className="text-xs text-neutral-400">
                  <div>at: {data.last_successful_send.accepted_at || data.last_successful_send.created_at}</div>
                  <div>provider msg-id: {data.last_successful_send.provider_message_id || "—"}</div>
                </div>
              ) : (
                <div className="text-xs text-neutral-500">nothing yet</div>
              )}
            </div>
            <div className="surface-1 p-4" data-testid="email-last-err">
              <div className="label-eyebrow mb-1">last provider error</div>
              {data.last_provider_error ? (
                <div className="text-xs">
                  <div className="text-red-400">{data.last_provider_error.status}</div>
                  <div className="text-neutral-500">{data.last_provider_error.created_at}</div>
                  <div className="text-neutral-400 break-all mt-1">
                    {data.last_provider_error.last_provider_error || "—"}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-neutral-500">nothing yet</div>
              )}
            </div>
          </div>

          <EmailReplyQueue />
          <SendTestEmailCard canSend={canSend} />
        </>
      )}
    </section>
  );
}

function SendTestEmailCard({ canSend }) {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function send() {
    if (!to.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/email/test", { to: to.trim() });
      setResult(data);
    } catch (e) {
      setResult({ ok: false, error: e?.response?.data?.detail || "send_failed" });
    } finally { setBusy(false); }
  }

  return (
    <div className="surface-1 p-4 space-y-3" data-testid="email-test-card">
      <div className="flex items-center gap-3">
        <div className="label-eyebrow flex-1">send test email</div>
        {!canSend && (
          <span className="text-xs text-amber-400">
            provider not ready — send will be blocked
          </span>
        )}
      </div>
      <div className="flex gap-2">
        <input
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="you@example.com"
          className="flex-1 bg-transparent border-b border-neutral-700 text-sm py-1 focus:outline-none focus:border-neutral-400"
          data-testid="email-test-to-input"
        />
        <button
          onClick={send}
          disabled={busy || !to.trim()}
          className="px-3 py-1.5 text-xs rounded bg-neutral-100 text-black disabled:opacity-40"
          data-testid="email-test-send-btn"
        >
          {busy ? "sending…" : "send test"}
        </button>
      </div>
      {result && (
        <div
          className={`text-xs p-2 rounded border ${
            result.ok
              ? "border-emerald-800 text-emerald-400"
              : "border-red-800 text-red-400"
          }`}
          data-testid="email-test-result"
        >
          {result.ok ? (
            <>
              ✓ accepted by provider · outbound_id=
              <span className="font-mono">{result.outbound_id}</span> ·
              provider_msg_id=<span className="font-mono">{result.message_id || "—"}</span>
            </>
          ) : (
            <>
              ✗ {result.error || result.reason || "unknown"}
              {result.outbound_id && (
                <> · outbound_id=<span className="font-mono">{result.outbound_id}</span></>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function statusOk(rec) {
  const s = (rec?.status || "").toLowerCase();
  return s === "verified" || s === "valid";
}

function PillBadge({ label, ok, value, testid }) {
  return (
    <div className="flex flex-col" data-testid={testid}>
      <span className="label-eyebrow">{label}</span>
      <span
        className={`text-xs px-2 py-0.5 rounded border ${
          ok
            ? "border-emerald-800 text-emerald-400"
            : "border-neutral-700 text-neutral-400"
        }`}
      >
        {value ?? "—"}
      </span>
    </div>
  );
}

function Counter({ label, v, testid, tone }) {
  const color = tone === "warn" && v > 0 ? "text-amber-400"
              : tone === "err"  && v > 0 ? "text-red-400"
              : "text-neutral-100";
  return (
    <div data-testid={testid}>
      <div className="label-eyebrow">{label}</div>
      <div className={`font-display text-xl font-light tabular-nums ${color}`}>{v ?? "—"}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
export function EmailReplyQueue() {
  const [replies, setReplies] = useState(null);
  const [filter, setFilter] = useState("unread");
  const [rev, setRev] = useState(0);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const { data } = await api.get("/admin/email/replies", {
          params: { status: filter, limit: 100 },
        });
        if (alive) setReplies(data.replies || []);
      } catch (e) { if (alive) setReplies([]); }
    }
    load();
    return () => { alive = false; };
  }, [filter, rev]);

  async function setStatus(replyId, status) {
    try {
      await api.patch(`/admin/email/replies/${replyId}`, { status });
      setRev((r) => r + 1);
    } catch (e) {
      alert(e?.response?.data?.detail || "update failed");
    }
  }

  return (
    <div className="surface-1 p-4 space-y-3" data-testid="email-reply-queue">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="label-eyebrow">reply queue</div>
        {["unread", "needs_response", "handled", ""].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setFilter(s)}
            className={`text-xs px-2 py-1 rounded border ${
              filter === s
                ? "bg-neutral-800 text-white border-neutral-600"
                : "border-neutral-800 text-neutral-500 hover:border-neutral-600"
            }`}
            data-testid={`email-reply-filter-${s || "all"}`}
          >
            {s || "all"}
          </button>
        ))}
      </div>

      {replies === null && <div className="text-xs text-neutral-500">Loading…</div>}
      {replies !== null && replies.length === 0 && (
        <div className="text-xs text-neutral-500" data-testid="email-reply-empty">
          No replies with status <span className="text-neutral-300">{filter || "any"}</span>.
        </div>
      )}
      {(replies || []).map((r) => (
        <div
          key={r.reply_id}
          className="border border-neutral-800 rounded p-3 space-y-2"
          data-testid={`email-reply-${r.reply_id}`}
        >
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-neutral-200 font-medium">{r.from_address}</span>
            <span className="text-neutral-500">→ {r.to_address}</span>
            <span className="text-neutral-600">{r.received_at}</span>
            <span className="ml-auto text-xs px-1.5 py-0.5 rounded border border-neutral-800 text-neutral-400">
              {r.status}
            </span>
          </div>
          <div className="text-sm text-neutral-200">{r.subject || "(no subject)"}</div>
          {r.linked_opportunity && (
            <div className="text-xs text-neutral-500">
              → {r.linked_opportunity.name}
              {r.linked_opportunity.city && `, ${r.linked_opportunity.city}`}
              {r.linked_opportunity.public_email && ` · ${r.linked_opportunity.public_email}`}
            </div>
          )}
          {r.linked_outbound && (
            <div className="text-xs text-neutral-500">
              re: “{r.linked_outbound.subject}” (sent {r.linked_outbound.created_at})
            </div>
          )}
          <pre className="text-xs text-neutral-300 whitespace-pre-wrap font-sans max-h-40 overflow-auto border-t border-neutral-900 pt-2">
            {r.text || "(empty body)"}
          </pre>
          <div className="flex gap-2">
            {r.status !== "handled" && (
              <button
                onClick={() => setStatus(r.reply_id, "handled")}
                className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-950"
                data-testid={`email-reply-mark-handled-${r.reply_id}`}
              >
                mark handled
              </button>
            )}
            {r.status !== "needs_response" && (
              <button
                onClick={() => setStatus(r.reply_id, "needs_response")}
                className="text-xs px-2 py-1 rounded border border-neutral-700 hover:border-neutral-400"
                data-testid={`email-reply-mark-needs-${r.reply_id}`}
              >
                needs response
              </button>
            )}
            {r.status !== "unread" && (
              <button
                onClick={() => setStatus(r.reply_id, "unread")}
                className="text-xs px-2 py-1 rounded border border-neutral-800 text-neutral-500 hover:border-neutral-600"
                data-testid={`email-reply-mark-unread-${r.reply_id}`}
              >
                mark unread
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default EmailHealthSection;
